import React, { ReactNode } from "react";
import { Form } from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";
import Branding from "../../config/Branding";

import { ANY_VALUE } from "../../constants";

type FilterDropdownProperties = {
    filter: DropdownFilter,
    callback: (qsParam: Record<string, string>) => void,
    value?: string
}

type FilterDropdownState = {
    value: string,
    selectedValues: Array<any>
}

export default class FilterDropdown extends React.Component<FilterDropdownProperties, FilterDropdownState> {
    private delimiter = ","; // Use comma as separator for multi-select values

    constructor(props: FilterDropdownProperties) {
        super(props);

        const isMultiSelect = props.filter.cardinality === "multiple";
        const incoming = (props.value || "").trim();
        if (props.filter.id === "maintopic" || props.filter.id === "excludeMaintopic") {
            this.delimiter = ";";
        }
        // For multi-select, convert incoming IDs to label objects for display
        const initialSelected = isMultiSelect && incoming.length
            ? incoming.split(this.delimiter).map(id => this.getOptionByIdOrLabel(id))
            : [];

        this.state = {
            value: props.value !== undefined ? props.value : this.props.filter.defaultValue,
            selectedValues: initialSelected
        };
    }

    // Helper to get option object by ID or label (for backward compatibility)
    private getOptionByIdOrLabel(idOrLabel: string): { value: string; label: string } {
        const filterValue = this.props.filter.values?.find(v => {
            if (typeof v === "string") return v === idOrLabel;
            return v.id === idOrLabel || v.label === idOrLabel;
        });

        if (typeof filterValue === "string") {
            return { value: filterValue, label: filterValue };
        } else if (filterValue) {
            return { value: filterValue.id, label: filterValue.label };
        }
        return { value: idOrLabel, label: idOrLabel };
    }

    public componentDidMount(): void {
        // Ensure multi-select shows initial values on first mount (no callback)
        if (this.props.filter.cardinality === "multiple") {
            const incoming = (this.props.value || "").trim();
            const selected = incoming.length
                ? incoming.split(this.delimiter).map(id => this.getOptionByIdOrLabel(id))
                : [];
            if (selected.length !== this.state.selectedValues.length) {
                this.setState({ selectedValues: selected });
            }
        }
    }

    public reset(): void {
        this.setState({
            value: this.props.filter.defaultValue,
            selectedValues: []
        });
    }

    public componentDidUpdate(prevProps: FilterDropdownProperties): void {
        if (prevProps.value !== this.props.value) {
            const isMultiSelect = this.props.filter.cardinality === "multiple";
            if (isMultiSelect) {
                const incoming = (this.props.value || "").trim();
                const ids = incoming.length ? incoming.split(this.delimiter) : [];
                const selected = ids.map(id => this.getOptionByIdOrLabel(id));
                this.setState({ selectedValues: selected });
            } else if (this.props.value !== undefined) {
                this.setState({ value: this.props.value });
            }
        }
    }

    public handleOnChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        this.setState({
            value: event.target.value
        });
        if (this.props.filter.qsParam) {
            const qsObj: Record<string, string> = {};
            if (event.target.value !== this.props.filter.defaultValue) {
                qsObj[this.props.filter.qsParam] = event.target.value;
            } else {
                qsObj[this.props.filter.qsParam] = "";
            }
            this.props.callback(qsObj);
        }
    }

    public handleMultiSelectChange = (selected: Array<any>): void => {
        this.setState({
            selectedValues: selected
        });
        if (this.props.filter.qsParam) {
            const qsObj: Record<string, string> = {};
            let qsParam = "";

            if (selected.length > 0) {
                // Send comma-separated values like Model Manager does
                const values = selected.map((item: any) => item.value || item);
            
                qsParam = values.join(this.delimiter);

                qsObj[this.props.filter.qsParam] = qsParam;
            } else {
                qsObj[this.props.filter.qsParam] = "";
            }
            this.props.callback(qsObj);
        }
    }

    public render(): ReactNode {
        const { filter } = this.props;
        const isMultiSelect = filter.cardinality === "multiple";

        if (isMultiSelect) {
            // Use Typeahead for multi-select filters (like Model Manager)
            // Map filter values to options with id as value and label for display
            const options = filter.values?.filter(value => value !== ANY_VALUE).map(value => {
                if (typeof value === "string") {
                    return { value: value, label: value };
                }
                return { value: value.id, label: value.label };
            }) || [];

            return (
                <Typeahead
                    id={filter.id}
                    multiple={true}
                    options={options}
                    selected={this.state.selectedValues}
                    placeholder={`Select ${filter.name} filter...`}
                    onChange={(selected: any) => this.handleMultiSelectChange(selected)}
                    clearButton
                />
            );
        } else {
            // Use regular dropdown for single-select filters
            // Use id as value and label for display
            return (
                <Form.Control
                    as="select"
                    id={filter.id}
                    title={filter.name}
                    value={this.state.value}
                    size="sm"
                    style={{ color: Branding.white, backgroundColor: "#1a1a1a", borderColor: "#333" }}
                    onChange={this.handleOnChange}>

                    {filter.values?.map((value, i) => {
                        if (typeof value === "string") {
                            return (
                                <option value={value} key={i}>
                                    {value}
                                </option>
                            );
                        }
                        return (
                            <option value={value.id} key={i}>
                                {value.label}
                            </option>
                        );
                    }) || null}
                </Form.Control>
            );
        }
    }
}
